import { FileText, FileCheck, CheckCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { DashboardStats } from "../hooks/useDashboardStats";
import { useTaxFilingsCount } from "@/features/tax-filings";

interface StatsCardsProps {
    stats: DashboardStats;
    isLoading: boolean;
}

interface StatCardProps {
    title: string;
    value: string | number;
    description: string;
    icon: React.ReactNode;
    iconBgClass?: string;
    iconClass?: string;
}

const StatCard = ({
    title,
    value,
    description,
    icon,
    iconBgClass = "bg-primary/10",
    iconClass = "text-primary",
}: StatCardProps) => (
    <Card className="hover:shadow-medium transition-shadow">
        <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4">
                <div className="space-y-1 min-w-0">
                    <p className="text-sm font-medium text-muted-foreground">
                        {title}
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold text-foreground">
                        {value}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                        {description}
                    </p>
                </div>
                <div className={cn("rounded-full p-3 shrink-0", iconBgClass)}>
                    <div className={iconClass}>{icon}</div>
                </div>
            </div>
        </CardContent>
    </Card>
);

const StatCardSkeleton = () => (
    <Card>
        <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4">
                <div className="space-y-3 flex-1">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-12 w-12 rounded-full shrink-0" />
            </div>
        </CardContent>
    </Card>
);

export const StatsCards = ({ stats, isLoading }: StatsCardsProps) => {
    const { count: taxFilingsCount, isLoading: taxFilingsLoading } =
        useTaxFilingsCount();

    if (isLoading) {
        return (
            <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                    <StatCardSkeleton key={i} />
                ))}
            </div>
        );
    }

    return (
        <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 content-loaded">
            <StatCard
                title="Total Documents"
                value={stats.totalDocuments}
                description="Files uploaded to your account"
                icon={<FileText className="h-6 w-6" />}
                iconBgClass="bg-primary/10"
                iconClass="text-primary"
            />
            <StatCard
                title="Tax Filings"
                value={taxFilingsLoading ? "—" : taxFilingsCount}
                description="Summaries, drafts, declarations & finals"
                icon={<FileCheck className="h-6 w-6" />}
                iconBgClass="bg-info/10"
                iconClass="text-info"
            />
            <StatCard
                title="Account Status"
                value="Active"
                description="All systems operational"
                icon={<CheckCircle className="h-6 w-6" />}
                iconBgClass="bg-success/10"
                iconClass="text-success"
            />
        </div>
    );
};
